import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/ui/dialog";
import { DialogBody } from "@/shared/ui/dialogBody";
import { Button } from "@/shared/ui/button";
import {
  useCreateProjectApiProjectsPost,
  useUploadProjectHeroImageApiProjectsProjectIdHeroImagePost,
  getReadProjectsApiProjectsGetQueryKey,
  getReadClientsApiClientsGetQueryKey,
  getReadContactsApiContactsGetQueryKey,
} from "@/type-gen/api";
import {
  ProjectForm,
  formDataToProjectCreate,
  type ProjectFormData,
} from "@/pages/client/components/projectForm";
import { HeroImageField } from "@/shared/components/heroImageField";

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId?: string; // When provided, client selection is hidden
  onCreated?: (projectId: string) => void;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  open,
  onOpenChange,
  clientId,
  onCreated,
}) => {
  const queryClient = useQueryClient();
  const createProjectMutation = useCreateProjectApiProjectsPost();
  const uploadHeroMutation =
    useUploadProjectHeroImageApiProjectsProjectIdHeroImagePost();
  const [heroFile, setHeroFile] = useState<File | null>(null);
  const isSaving =
    createProjectMutation.isPending || uploadHeroMutation.isPending;

  const {
    register,
    control,
    handleSubmit: handleFormSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm<ProjectFormData>({
    defaultValues: {
      name: "",
      description: "",
      location: "",
      shoot_date: "",
    },
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      reset({
        name: "",
        description: "",
        location: "",
        shoot_date: "",
      });
      setHeroFile(null);
    }
  }, [open, reset]);

  const onSubmit = async (data: ProjectFormData) => {
    // Validate client selection when clientId is not provided
    if (!clientId && !data.client_id && !data.new_client_name?.trim()) {
      setError("client_id", {
        type: "manual",
        message: "Please select a client or enter a new client name",
      });
      setError("new_client_name", {
        type: "manual",
        message: "Please select a client or enter a new client name",
      });
      return;
    }

    const projectData = formDataToProjectCreate(data, clientId);
    const created = await createProjectMutation.mutateAsync({
      data: projectData,
    });

    if (heroFile) {
      await uploadHeroMutation
        .mutateAsync({
          projectId: created.data.id,
          data: { file: heroFile },
        })
        .catch(() => undefined);
    }

    queryClient.invalidateQueries({
      queryKey: getReadProjectsApiProjectsGetQueryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: getReadClientsApiClientsGetQueryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: getReadContactsApiContactsGetQueryKey(),
    });

    onOpenChange(false);
    onCreated?.(created.data.id);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} isLoading={isSaving}>
      <DialogContent className="bg-card border-border p-0" isLoading={isSaving}>
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-foreground">Create Project</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleFormSubmit(onSubmit)}
          className="flex flex-col flex-1 min-h-0"
        >
          <DialogBody>
            <div className="space-y-4">
              <HeroImageField
                file={heroFile}
                onChange={setHeroFile}
                disabled={isSaving}
              />
              <ProjectForm
                register={register}
                control={control}
                errors={errors}
                clientId={clientId}
              />
            </div>
          </DialogBody>
          <DialogFooter className="px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="border-border text-foreground hover:bg-accent"
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              Create Project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
